export type PaymentReleaseSubMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  paymentRequestStatus?: string;
};

export type PaymentReleaseMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  subMilestones?: PaymentReleaseSubMilestoneSelection[] | null;
};

export type PaymentReleaseRequestToSend =
  | { kind: "milestone"; milestoneId: string; name: string }
  | { kind: "sub_milestone_batch"; milestoneId: string; subMilestones: PaymentReleaseSubMilestoneSelection[] };

function sumSelectedSubBudgets(subs: PaymentReleaseSubMilestoneSelection[]): number {
  return subs.reduce((sum, sub) => sum + Number(sub.budgetAmount ?? 0), 0);
}

function isSubPaymentApproved(sub: PaymentReleaseSubMilestoneSelection): boolean {
  return String(sub.paymentRequestStatus ?? "").toLowerCase() === "approved";
}

function getUnreleasedSubs(milestone: PaymentReleaseMilestoneSelection): PaymentReleaseSubMilestoneSelection[] {
  return (milestone.subMilestones ?? []).filter((sub) => !isSubPaymentApproved(sub));
}

function getApprovedSubBudgetTotal(milestone: PaymentReleaseMilestoneSelection): number {
  return sumSelectedSubBudgets((milestone.subMilestones ?? []).filter(isSubPaymentApproved));
}

/** Remaining parent budget after already-approved same-milestone subs. */
function getRemainingMilestoneBudget(milestone: PaymentReleaseMilestoneSelection): number {
  const milestoneBudget = Number(milestone.budgetAmount ?? 0);
  return Math.max(0, milestoneBudget - getApprovedSubBudgetTotal(milestone));
}

function selectedSubsCoverRemaining(
  selectedSubs: PaymentReleaseSubMilestoneSelection[],
  remainingBudget: number
): boolean {
  return remainingBudget > 0 && sumSelectedSubBudgets(selectedSubs) >= remainingBudget - 0.01;
}

/**
 * Builds payment-release API payloads from selected milestone/sub-milestone ids.
 * Parent + subs that cover the remaining milestone budget emit a milestone request only
 * (including after partial sub releases, e.g. last sub equals remaining escrow).
 * Sub-only selections (or parent + partial subs) emit a sub-milestone batch, with an
 * extra milestone request when the parent is also selected for the remainder.
 */
export function buildPaymentReleaseRequestsToSend(
  selectedItemIds: Iterable<string>,
  milestones: PaymentReleaseMilestoneSelection[]
): PaymentReleaseRequestToSend[] {
  const selected = new Set(selectedItemIds);
  const result: PaymentReleaseRequestToSend[] = [];

  for (const milestone of milestones) {
    const parentSelected = selected.has(milestone.id);
    const selectedSubs = (milestone.subMilestones ?? []).filter((sub) => selected.has(sub.id));
    const remainingBudget = getRemainingMilestoneBudget(milestone);

    if (selectedSubs.length > 0) {
      const selectedSubTotal = sumSelectedSubBudgets(selectedSubs);
      const coversRemaining = selectedSubsCoverRemaining(selectedSubs, remainingBudget);

      if (parentSelected && coversRemaining) {
        result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
        continue;
      }

      result.push({
        kind: "sub_milestone_batch",
        milestoneId: milestone.id,
        subMilestones: selectedSubs.map((sub) => ({ id: sub.id, name: sub.name, budgetAmount: sub.budgetAmount })),
      });

      if (parentSelected && remainingBudget > 0 && selectedSubTotal < remainingBudget - 0.01) {
        result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
      }
      continue;
    }

    if (parentSelected) {
      const unreleasedSubs = getUnreleasedSubs(milestone);
      if (unreleasedSubs.length > 0) {
        result.push({
          kind: "sub_milestone_batch",
          milestoneId: milestone.id,
          subMilestones: unreleasedSubs.map((sub) => ({
            id: sub.id,
            name: sub.name,
            budgetAmount: sub.budgetAmount,
            paymentRequestStatus: sub.paymentRequestStatus,
          })),
        });
        continue;
      }
      result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
    }
  }

  return result;
}
